import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { Pagination, ReceivingArea, ReplenishmentPlan } from '@ekkogmbh/apisdk';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import useRequest from 'src/Common/Hooks/useRequest';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import PaginatedSelect from 'src/Common/Components/Forms/PaginatedSelect';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import CoordinateListItem from 'src/Common/Components/CoordinateListItem';

interface Props {
  receivingArea?: ReceivingArea;
  closeHandler: () => void;
}

export const ReceivingAreaPanel = ({ receivingArea, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { enableSave } = usePanel();
  const { scrollTop } = useNavigation();
  const { refresh } = usePagination();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [replenishmentPlan, setReplenishmentPlan] = useState<ReplenishmentPlan>();

  const [, startLoading] = useRequest();
  const fetchReplenishmentPlans = useCallback((pagination: Pagination) => api.getReplenishmentPlans(pagination), [api]);

  const savePayload = useMemo(() => {
    if ([name, coordinate].includes('') || replenishmentPlan === undefined) {
      return undefined;
    }

    return {
      name,
      coordinate,
      replenishmentPlan,
    } as ReceivingArea;
  }, [name, coordinate, replenishmentPlan]);

  useEffect(() => {
    enableSave(savePayload !== undefined);
  }, [savePayload]);

  const reset = useCallback(() => {
    setName(receivingArea?.name ?? '');
    setCoordinate(receivingArea?.coordinate ?? '');
    setReplenishmentPlan(receivingArea?.replenishmentPlan);
  }, [receivingArea]);

  const save = useCallback(() => {
    if (savePayload === undefined) {
      return;
    }

    startLoading(
      () => api.saveReceivingArea(savePayload),
      () => {
        refresh();
        scrollTop();
        closeHandler();
      },
    );
  }, [api, savePayload, closeHandler]);

  useEffect(() => {
    reset();
  }, [reset]);

  return (
    <ContentPanel
      steps={[
        {
          title: 'Receiving Area',
          contentCallback: () => (
            <CenteredColumn width={'72%'}>
              <StyledTextField
                type={'text'}
                label={'Name'}
                value={name}
                disabled={receivingArea !== undefined}
                onChange={(e) => setName(String(e.target.value))}
              />
              <CoordinateInput
                value={coordinate}
                disabled={receivingArea !== undefined}
                onChange={(coordinate) => setCoordinate(coordinate)}
                trailingDelimiter={false}
              />
              <PaginatedSelect
                label={'Replenishment Plan'}
                value={replenishmentPlan}
                itemId={(item) => item.name + '::' + item.coordinate}
                itemRender={(item) => <CoordinateListItem label={item.name} coordinate={item.coordinate} />}
                onChange={(item) => setReplenishmentPlan(item as ReplenishmentPlan | undefined)}
                fetchPaginatedItems={fetchReplenishmentPlans}
              />
            </CenteredColumn>
          ),
        },
      ]}
      onCancel={closeHandler}
      onReset={reset}
      onSave={save}
    />
  );
};
